import { FunctionComponent, useMemo } from 'react'

import { SportEntitiesData } from '@utils/entities'
import translate from '@i18n'
import useFmtCompetitionEntityIdsSorted from '@hooks/useFmtCompetitionEntityIdsSorted'
import useSportEntities from '@hooks/useSportEntities'

import GroupedCompetitorsItem from './GroupedCompetitorsItem'

type GroupedCompetitorsListProps = {
  currentFilterId: string
  currentSearchTerm?: string
}

type CompetitorTableData = {
  id: string
  title?: string
  data: SportEntitiesData
}

type CompetitorsTables = CompetitorTableData[]

const GroupedCompetitorsList: FunctionComponent<
  GroupedCompetitorsListProps
> = ({ currentFilterId, currentSearchTerm }) => {
  const competitionIds = useFmtCompetitionEntityIdsSorted()
  const {
    getSportEntityById,
    getSportEntitiesList,
    getFollowableCompetitorsByCompetitionId,
    getFollowableCompetitorsWithNoCompetitionId,
  } = useSportEntities()

  const followableCompetitors = useMemo(
    () =>
      getSportEntitiesList({
        onlyEntityType: 'competitor',
        onlyIsFollowable: true,
      }),
    [getSportEntitiesList],
  )

  const competitorsFilteredBySearchTerm = useMemo<CompetitorsTables>(() => {
    const normalizedSearchTerm = currentSearchTerm?.trim().toLowerCase()

    if (!normalizedSearchTerm) {
      return []
    }

    return [
      {
        id: `filtered-by-search-term-${normalizedSearchTerm}`,
        data: followableCompetitors.filter((item) =>
          item.title.toLowerCase().includes(normalizedSearchTerm),
        ),
      },
    ]
  }, [followableCompetitors, currentSearchTerm])

  const allGroupedCompetitors = useMemo<CompetitorsTables>(
    () =>
      competitionIds.reduce(
        (acc, competitionId, competitionIdIndex, competitionIdsArray) => {
          acc.push({
            id: `competition-${competitionId}`,
            title: getSportEntityById(competitionId)?.title,
            data: getFollowableCompetitorsByCompetitionId(competitionId),
          })

          if (competitionIdIndex === competitionIdsArray.length - 1) {
            const competitorsWithNoCompetitionId =
              getFollowableCompetitorsWithNoCompetitionId()

            if (competitorsWithNoCompetitionId.length > 0) {
              acc.push({
                id: 'no-competition-id',
                title: translate('fmtFilter.otherCompetitions'),
                data: competitorsWithNoCompetitionId,
              })
            }
          }

          return acc
        },
        [] as CompetitorsTables,
      ),
    [
      competitionIds,
      getFollowableCompetitorsByCompetitionId,
      getFollowableCompetitorsWithNoCompetitionId,
      getSportEntityById,
    ],
  )

  const groupedCompetitorsFilteredByCompetitionId =
    useMemo<CompetitorsTables>(() => {
      if (!currentFilterId) {
        return []
      } else if (currentFilterId === 'other') {
        return [
          {
            id: 'no-competition-id',
            data: getFollowableCompetitorsWithNoCompetitionId(),
          },
        ]
      } else {
        return [
          {
            id: `competition-${currentFilterId}`,
            data: getFollowableCompetitorsByCompetitionId(currentFilterId),
          },
        ]
      }
    }, [
      currentFilterId,
      getFollowableCompetitorsByCompetitionId,
      getFollowableCompetitorsWithNoCompetitionId,
    ])

  const tablesToShow = currentSearchTerm
    ? competitorsFilteredBySearchTerm
    : currentFilterId
      ? groupedCompetitorsFilteredByCompetitionId
      : allGroupedCompetitors

  return (
    <>
      {tablesToShow.map((table) => (
        <GroupedCompetitorsItem
          key={table.id}
          competitionTitle={table.title}
          competitorsListData={table.data}
        />
      ))}
    </>
  )
}

export default GroupedCompetitorsList
